import {useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {linkify} from "../../lib/linkify";
import {Card} from "@components/shadcn-ui/card";
import {getTaskById, getUsers, updateTask} from "../../features/tasks/services/tasksService";
import {Task, TASK_TYPE, TaskClosureReason, TaskStatus, TaskType} from "../../features/tasks/types";
import {Button} from "@components/shadcn-ui/button";
import {Input} from "@components/shadcn-ui/input";
import {Textarea} from "@components/shadcn-ui/textarea";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@components/shadcn-ui/select";
import {useToast} from "@customHooks/useToast";
import {encodeURIComponent, getResourcePath} from "@lib/utils";
import {Combobox} from "@components/shadcn-ui/combobox";
import {ResourceNotes} from "../../features/resourceNotes/ResourceNotes";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@components/shadcn-ui/tabs";
import {FileUploadComponent} from "../../features/createApplication/components/FileUploadComponent";
import {MobileFileManager} from "../../features/createApplication/components/DisplayApplicationIDContent";
import {TASK_PAGE_DOCUMENT_CATEGORIES} from "../../features/createApplication/components/FileCategoryModal";
import Snipper from "@components/ui/Snipper";
import {applicationService} from "../../features/createApplication/service/applicationService";

export const TaskDetailsPage = () => {
    const { taskId } = useParams<{ taskId: string }>();
    const [task, setTask] = useState<Task | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [editedTask, setEditedTask] = useState<Partial<Task> & { watchersString?: string, labelsString?: string }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { toast } = useToast();
    const [users, setUsers] = useState([]);
    const [isLoadingUsers, setIsLoadingUsers] = useState(true);

    useEffect(() => {
        const fetchTask = async () => {
            if (taskId) {
                try {
                    const fetchedTask = await getTaskById(taskId);
                    setTask(fetchedTask);
                    setEditedTask({
                        ...fetchedTask,
                        watchersString: fetchedTask.watchers.join(','),
                        labelsString: fetchedTask.labels.join(',')
                    });
                } catch (error) {
                    console.error("Error fetching task:", error);
                    toast({
                        variant: "destructive",
                        description: `Failed to fetch task details (${error})`,
                    });
                } finally {
                    setIsLoading(false);
                }
            }
        };

        const fetchUsers = async () => {
            try {
                const fetchedUsers = await getUsers();
                setUsers(fetchedUsers);
            } catch (error) {
                console.error("Error fetching users:", error);
                toast({
                    variant: "destructive",
                    description: "Failed to fetch users"
                });
            } finally {
                setIsLoadingUsers(false);
            }
        };

        fetchTask();
        fetchUsers();
    }, [taskId]);

    const handleEdit = () => {
        setEditedTask({
            ...task,
            watchersString: task?.watchers.join(',') || '',
            labelsString: task?.labels.join(',') || ''
        });
        setIsEditing(true);
    };

    const handleCancel = () => {
        setEditedTask({
            ...task,
            watchersString: task?.watchers.join(',') || '',
            labelsString: task?.labels.join(',') || ''
        });
        setIsEditing(false);
    };

    const handleInputChange = (
        field: keyof Task,
        value: string | string[] | number | boolean | TaskStatus | TaskClosureReason
    ) => {
        setEditedTask((prev) => ({ ...prev, [field]: value }));
    };

    const handleWatcherChange = (value: string) => {
        setEditedTask(prev => ({
            ...prev,
            watchersString: value,
            watchers: value.split(',').map(w => w.trim()).filter(Boolean)
        }));
    };

    const handleLabelChange = (value: string) => {
        setEditedTask(prev => ({
            ...prev,
            labelsString: value,
            labels: value.split(',').map(l => l.trim()).filter(Boolean)
        }));
    };

    // Check if closure status is required based on task status
    const isClosureReasonRequired = editedTask.status === TaskStatus.COMPLETE || editedTask.status === TaskStatus.CANCELLED;

    const handleSubmit = async () => {
        if (!taskId || !editedTask) return;

        // Validate closure status if required
        if (isClosureReasonRequired && !editedTask.closureReason) {
            toast({
                variant: "destructive",
                description: "Closure status is required when task is Complete or Cancelled"
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const updatedTask = await updateTask({
                taskId,
                assignee: editedTask.assignee!,
                status: editedTask.status!,
                dueDateInUTCEpoch: new Date(editedTask.dueDateInUTCEpoch!).getTime(),
                description: editedTask.description!,
                title: editedTask.title!,
                watchers: editedTask.watchers!,
                labels: editedTask.labels!,
                priority: editedTask.priority!,
                isBlocked: editedTask.isBlocked!,
                closureReason: editedTask.closureReason as string,
            });

            setTask(updatedTask);

            //TODO: Remove hack. Later change to have check on taskType == VERIFICATION.
            if (updatedTask.title.trim().toLowerCase().indexOf("address verification request") != -1
                && updatedTask.status == TaskStatus.COMPLETE) {

                const data = await applicationService.getApplicationById(updatedTask.resourceId);

                await applicationService.performAction(
                    data,
                    "VERIFICATION_COMPLETE",
                    data.leadId
                );
            }

            setIsEditing(false);
            toast({ description: "Task updated successfully!" });
        } catch (error) {
            console.error("Error updating task:", error);
            toast({
                variant: "destructive",
                description: "Failed to update task",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const taskAttachmentsId = task?.resourceId + "#" + task?.taskId;

    if (isLoading)
        return <div className="flex items-center justify-center gap-2 w-full">
        <Snipper/>
        <p>Loading task details...</p>
    </div>;
    if (!task) return <div className="flex items-center justify-center gap-2 w-full">
        <p>Task not found...</p>
    </div>;

    return (
        <div>
            <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Task Details</h2>
                    {!isEditing ? (
                        task.status === TaskStatus.COMPLETE || task.status === TaskStatus.CANCELLED ? null : (<Button onClick={handleEdit}>Edit Task</Button>)
                    ) : (
                        <div className="space-x-2">
                            <Button onClick={handleSubmit} disabled={isSubmitting}>
                                {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                            <Button variant="outline" onClick={handleCancel}>
                                Cancel
                            </Button>
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4">
                        <div>
                            <label className="font-semibold">Title:</label>
                            {isEditing ? (
                                <Input
                                    value={editedTask.title}
                                    onChange={(e) => handleInputChange('title', e.target.value)}
                                />
                            ) : (
                                <p>{linkify(task.title)}</p>
                            )}
                        </div>
                        <div className="col-span-2">
                            <label className="font-semibold">Description:</label>
                            {isEditing ? (
                                <Textarea
                                    value={editedTask.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    className="mt-1"
                                />
                            ) : (
                                <p>{linkify(task.description)}</p>
                            )}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-semibold">Due Date:</label>
                            {isEditing ? (
                                <Input
                                    type="date"
                                    value={new Date(editedTask.dueDateInUTCEpoch!).toISOString().slice(0, 16)}
                                    onChange={(e) => handleInputChange('dueDateInUTCEpoch', new Date(e.target.value).getTime())}
                                />
                            ) : (
                                <p>{new Date(task.dueDateInUTCEpoch).toLocaleString()}</p>
                            )}
                        </div>

                        <div>
                            <label className="font-semibold">Status:</label>
                            {isEditing ? (
                                <Select
                                    value={editedTask.status}
                                    onValueChange={(value) => handleInputChange('status', value as TaskStatus)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.values(TaskStatus).map((status) => (
                                            <SelectItem key={status} value={status}>
                                                {status}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p>{task.status}</p>
                            )}
                        </div>

                        {/* Closure Status field - shown conditionally */}
                        {(isEditing && isClosureReasonRequired) || (!isEditing && task.closureReason) ? (
                            <div>
                                <label className="font-semibold">
                                    Closure Reason:
                                    {isEditing && isClosureReasonRequired &&
                                        <span className="text-red-500">*</span>}
                                </label>
                                {isEditing ? (
                                    <Select
                                        value={editedTask.closureReason}
                                        onValueChange={(value) => handleInputChange('closureReason', value as TaskClosureReason)}
                                        required={isClosureReasonRequired}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select closure reason"/>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.values(TaskClosureReason).map((status) => (
                                                <SelectItem key={status} value={status}>
                                                    {status.replace(/_/g, ' ')}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                ) : (
                                    <p>{task.closureReason ? task.closureReason.replace(/_/g, ' ') : 'Not specified'}</p>
                                )}
                            </div>
                        ) : null}

                        <div>
                            <label className="font-semibold">Assignee:</label>
                            {isEditing ? (
                                <Combobox
                                    options={users}
                                    value={editedTask.assignee as string}
                                    onValueChange={(value) => handleInputChange('assignee', value)}
                                    placeholder="Select assignee"
                                />
                            ) : (
                                <p>{task.assignee}</p>
                            )}
                        </div>

                        <div>
                            <label className="font-semibold">Author:</label>
                            <p>{task.author}</p>
                        </div>
                        <div>
                            <label className="font-semibold">Task Type</label>
                            <p>{task.type}</p>

                            {/*<label className="font-semibold">Priority:</label>
                            {isEditing ? (
                                <Select
                                    value={editedTask.priority?.toString()}
                                    onValueChange={(value) => handleInputChange('priority', parseInt(value))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select priority"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[1, 2, 3, 4, 5].map((p) => (
                                            <SelectItem key={p} value={p.toString()}>
                                                {p}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p>{task.priority}</p>
                            )}*/}
                        </div>

                        {/*<div>
                            <label className="font-semibold">Is Blocked:</label>
                            {isEditing ? (
                                <Select
                                    value={editedTask.isBlocked?.toString()}
                                    onValueChange={(value) => handleInputChange('isBlocked', value === 'true')}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select blocked status"/>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="true">Yes</SelectItem>
                                        <SelectItem value="false">No</SelectItem>
                                    </SelectContent>
                                </Select>
                            ) : (
                                <p>{task.isBlocked ? 'Yes' : 'No'}</p>
                            )}
                        </div>*/}
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                        {/* <div>
                        <label className="font-semibold">Watchers:</label>
                        {isEditing ? (
                            <Input
                                value={editedTask.watchersString}
                                onChange={(e) => handleWatcherChange(e.target.value)}
                                placeholder="Enter watchers (comma-separated)"
                            />
                        ) : (
                            <p>{task.watchers.join(', ')}</p>
                        )}
                    </div>

                    <div>
                        <label className="font-semibold">Labels:</label>
                        {isEditing ? (
                            <Input
                                value={editedTask.labelsString}
                                onChange={(e) => handleLabelChange(e.target.value)}
                                placeholder="Enter labels (comma-separated)"
                            />
                        ) : (
                            <p>{task.labels.join(', ')}</p>
                        )}
                    </div>*/}

                            <div>
                                <p className="text-sm text-gray-500 font-medium">TaskId: {task.taskId}</p>
                                <p className="text-sm text-gray-500 font-medium">Resource: <Link
                                    style={{textDecoration: "underline"}}
                                    to={`${getResourcePath(task)}/${encodeURIComponent(task.resourceId)}`}>
                                    {task.resourceId}
                                </Link>
                                </p>

                            </div>
                        </div>
                    </div>
            </Card>
            <Card className="mt-4">
                <Tabs defaultValue="notes" className="w-full">
                    <TabsList className="w-full">
                        <TabsTrigger value="notes" className="flex-1">
                            Notes
                            {/*<Badge variant="secondary" className="ml-2">*/}
                            {/*    {notes?.length || 0}*/}
                            {/*</Badge>*/}
                        </TabsTrigger>
                        <TabsTrigger value="attachments" className="flex-1">
                            Attachments
                            {/*<Badge variant="secondary" className="ml-2">*/}
                            {/*    {attachments?.length || 0}*/}
                            {/*</Badge>*/}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="notes">
                        <div className="p-4">
                            <ResourceNotes
                                resourceId={task.taskId}
                                resourceType={TASK_TYPE}
                            />
                        </div>
                    </TabsContent>

                    <TabsContent value="attachments">
                        <div className="p-4">
                            <FileUploadComponent
                                id={taskAttachmentsId}
                                documentCategories={TASK_PAGE_DOCUMENT_CATEGORIES} // Specify allowed categories
                            />
                            <div className="mt-4">
                                <MobileFileManager
                                    applicationId={taskAttachmentsId}
                                    documentCategories={TASK_PAGE_DOCUMENT_CATEGORIES} // Specify allowed categories
                                />
                            </div>
                        </div>
                    </TabsContent>


                    {/*<TabsContent value="attachments">*/}
                    {/*    <div className="p-4">*/}
                    {/*        <FileUploadComponent id={task.resourceId} />*/}
                    {/*        <div className="mt-4">*/}
                    {/*            <MobileFileManager applicationId={task.resourceId} />*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*</TabsContent>*/}
                </Tabs>
            </Card>
        </div>
    );
};
